import { useMemo } from 'react';

import { ActivityBuilderModal } from 'approot/activity-builder/activity-builder-modal';
import { SimpleModalVerticalActionsStyled } from 'lib/modal/simple-modal.styles';
import { NegativeButton } from 'approot/shared/styles/buttons/negative-button';
import { PStyled, UlStyled } from 'approot/shared/styles/typography.style';
import { red9 } from 'approot/shared/styles/colours.style';
import { ActivitiesContentType, LESSON_ACTIVITIES } from 'global/constants';
import Bouncer from 'approot/shared/bouncer/bouncer';
import { getChangesForResponseComponents } from './logic/get-changes-for-response-components';
import { useGetInteractiveTemplates } from 'approot/activity-builder/components/components-menu/component-menu.hooks';
import useGetActivitiesChanges from './use-get-activities-changes';

type Props = {
  contentId: number;
  contentType: ActivitiesContentType;
  onMinorUpdateSelected: () => void;
  onMajorUpdateSelected: () => void;
  closeModal: () => void;
};

const SettingsReviewChangesModal = ({
  contentId,
  contentType,
  onMinorUpdateSelected,
  onMajorUpdateSelected,
  closeModal,
}: Props) => {
  const { data: templatesData } = useGetInteractiveTemplates();

  const { data } = useGetActivitiesChanges({
    contentId,
    contentType,
  });

  const responseComponentsWithAnswers = useMemo(
    () => getChangesForResponseComponents(data?.getActivitiesChanges || []),
    [data?.getActivitiesChanges]
  );

  return (
    <ActivityBuilderModal
      title="Review Changes to Answered Components"
      headerTitle="Review Changes to Answered Components"
      isVisible
      size={500}
      onClose={closeModal}
    >
      <PStyled colour={red9}>
        <strong>
          We’ve found student answers linked to the components in this{' '}
          {contentType === LESSON_ACTIVITIES ? 'lesson' : 'assessment'}.
        </strong>
      </PStyled>
      <PStyled colour={red9}>
        Please review the list and compare these components to your changes. If
        you've only made small, non-meaningful text updates (like typo fixes),
        you can continue with a minor update.
      </PStyled>
      <PStyled colour={red9}>
        If your changes are larger — like reordering, changing the meaning of
        questions, or altering key content — please publish a major update
        instead.
      </PStyled>

      {responseComponentsWithAnswers.length === 0 ? (
        <Bouncer />
      ) : (
        <>
          <PStyled>
            <strong>Components with answers</strong>
          </PStyled>
          <UlStyled>
            {responseComponentsWithAnswers.map(item => (
              <li key={item.componentId}>
                {`
                  ${
                    templatesData?.getComponentTemplates.find(
                      t =>
                        t?.data.componentProperties.type === item.componentType
                    )?.name
                  }${
                    item.change === 'deleted'
                      ? ' deleted'
                      : ` on ${item.sectionNumber} ${item.change}`
                  }
                `}
              </li>
            ))}
          </UlStyled>
        </>
      )}
      <SimpleModalVerticalActionsStyled>
        <NegativeButton
          type="button"
          disabled={!data}
          onClick={() => {
            closeModal();
            onMinorUpdateSelected();
          }}
        >
          Continue with Minor Update
        </NegativeButton>
        <NegativeButton
          type="button"
          variant="outlined"
          disabled={!data}
          onClick={() => {
            closeModal();
            onMajorUpdateSelected();
          }}
        >
          Continue with Major Update
        </NegativeButton>
      </SimpleModalVerticalActionsStyled>
    </ActivityBuilderModal>
  );
};

export default SettingsReviewChangesModal;
